/**
 * A site's emergency account (D20): whether it has one and since when; create
 * it, or rotate its password. The password is in the create/rotate answer
 * only — this shows it once (`revealed`), with a copy button, until the admin
 * dismisses it. It signs in on the site's nodes only while they cannot reach
 * the cloud, with operator rights on the site.
 */
import { CopyIcon, KeyRoundIcon } from 'lucide-react';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';

export type EmergencyAccountCardProps = {
  readonly status: { readonly exists: boolean; readonly rotatedAt: string | null } | null;
  /** The password just set, shown this once; null otherwise. */
  readonly revealed: string | null;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onCreate: () => void;
  readonly onRotate: () => void;
  readonly onCopy: (text: string) => void;
  readonly onDismiss: () => void;
};

export function EmergencyAccountCard({ status, revealed, pending, error, onCreate, onRotate, onCopy, onDismiss }: EmergencyAccountCardProps) {
  return (
    <Card data-testid="emergency-account">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRoundIcon className="size-4" aria-hidden="true" />
          Emergency account
        </CardTitle>
        <CardDescription>Signs in on this site’s servers only while they cannot reach the cloud — every use is audited. Keep the password offline, in the studio.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3">
        {status === null ? <p className="text-sm text-muted-foreground">Loading…</p> : null}
        {status !== null && !status.exists ? <p className="text-sm">This site has no emergency account.</p> : null}
        {status?.exists === true ? <p className="text-sm">Password set {status.rotatedAt?.slice(0, 16).replace('T', ' ') ?? '?'} UTC.</p> : null}
        {revealed === null ? null : (
          <div role="status" className="grid gap-2 rounded-md border border-warning p-3" data-testid="emergency-password">
            <p className="text-sm font-medium">The password — shown this once:</p>
            <code className="select-all break-all rounded bg-muted px-2 py-1 font-mono text-sm">{revealed}</code>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => onCopy(revealed)} data-testid="copy-emergency-password">
                <CopyIcon />
                Copy
              </Button>
              <Button size="sm" onClick={onDismiss} data-testid="dismiss-emergency-password">
                I have stored it
              </Button>
            </div>
          </div>
        )}
        {error === null ? null : (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        {status === null ? null : status.exists ? (
          <Button variant="outline" className="w-fit" disabled={pending} onClick={onRotate} data-testid="rotate-emergency">
            Rotate password
          </Button>
        ) : (
          <Button className="w-fit" disabled={pending} onClick={onCreate} data-testid="create-emergency">
            Create emergency account
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
